import React, { useContext, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ThemeContext } from '../ThemeContext';
import { Target, Users, Award, Moon, Zap, TrendingUp } from 'lucide-react';

// Module-level animation state tracking
const cardAnimationState = new Map();

// Card component moved outside to prevent re-creation
const Card = ({ children, className = '', delay = 0, cardId }) => {
  const hasAnimated = cardAnimationState.has(cardId) ? cardAnimationState.get(cardId) : false;

  React.useEffect(() => {
    if (!hasAnimated) {
      cardAnimationState.set(cardId, true);
    }
  }, [cardId, hasAnimated]);

  return (
    <motion.div
      initial={hasAnimated ? false : { opacity: 0, y: 50, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: 0.6,
        delay: hasAnimated ? 0 : delay,
        type: "spring",
        stiffness: 100,
      }}
      whileHover={{
        scale: 1.02,
        y: -5,
        transition: { duration: 0.2 },
      }}
      className={`relative backdrop-blur-xl bg-white/80 dark:bg-gray-800/80 rounded-3xl shadow-2xl p-6 md:p-8 border border-white/20 dark:border-gray-700/50 ${className}`}
      style={{
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
      }}
    >
      {children}
    </motion.div>
  );
};

const AboutGamePage = () => {
  const { isDarkMode } = useContext(ThemeContext);
  const navigate = useNavigate();

  // Memoize title gradient to update when dark mode changes
  const titleGradient = useMemo(() => {
    const color = isDarkMode ? '#ff7a00' : '#212D82';
    return `linear-gradient(90deg, ${color} 0%, ${color} 40%, rgba(255, 122, 0, 0.8) 50%, ${color} 60%, ${color} 100%)`;
  }, [isDarkMode]);

  const sectionHeadingGradient = useMemo(() => {
    const color = isDarkMode ? '#ff7a00' : '#212D82';
    return `linear-gradient(90deg, ${color} 0%, ${color} 40%, rgba(255, 122, 0, 0.8) 50%, ${color} 60%, ${color} 100%)`;
  }, [isDarkMode]);

  const features = [
    {
      icon: <Moon size={32} />,
      title: 'Dark Mode Designed for Deep Focus',
      description: 'Prepare for hours without eye strain. Dark-mode interface reduces visual fatigue, keeping you focused on the challenge at hand. Built for serious preparation.',
    },
    {
      icon: <Target size={32} />,
      title: 'Genderless, Focus-First Interface',
      description: 'Our design is clean, geometric, and distraction-free: no generic mascots or gendered tropes. Your progress is tracked by skill and mastery, with a focus entirely on your intellectual capabilities.',
    },
    {
      icon: <Zap size={32} />,
      title: 'Adaptive Learning Technology',
      description: 'Our system learns what you know and adapts in real-time to challenge your areas of opportunity, focusing your energy where it counts.',
    },
    {
      icon: <TrendingUp size={32} />,
      title: 'Earn Experience, Not Grades',
      description: 'Practice on your own or challenge friends. Earn points for correct answers and lose points for incorrect answers. Build up points to put yourself at the top of the leaderboard.',
    },
  ];

  return (
    <div className={`min-h-screen pt-14 pb-24 pl-0 md:pl-24 overflow-hidden bg-gradient-to-br from-sky-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900`}>
      <div className="relative z-20 px-4 sm:px-6 lg:px-8 py-6 md:py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, type: "spring" }}
          className="text-center mb-8 md:mb-12"
        >
          <h1
            key={`about-game-title-${isDarkMode}`}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold font-zonaProUltra mb-4 relative inline-block"
            style={{
              background: titleGradient,
              backgroundSize: '200% 100%',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              animation: 'shine 10s linear infinite',
              lineHeight: '1.2',
              paddingBottom: '0.25rem'
            }}
          >
            Game Your SAT: Level Up Your Score.
          </h1>
          <motion.p
            className="text-2xl sm:text-3xl md:text-4xl font-bold font-zonaProUltra text-gray-800 dark:text-gray-200 mb-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            Stop Studying. Start Winning.
          </motion.p>
          <motion.p
            className="text-base sm:text-lg md:text-xl text-gray-600 dark:text-gray-400 max-w-4xl mx-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            Tired of dry textbooks and endless practice drills? <span className="font-bold font-zonaProUltra">Game Your SAT</span> transforms tedious prep into an enjoyable challenge. We use cutting-edge learning technology wrapped in a fun, distraction-free interface to help you achieve your best SAT score.
          </motion.p>
        </motion.div>

        {/* How It Works Section */}
        <Card delay={0.2} cardId="how-it-works" className="mb-8 md:mb-12">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            <h2
              key={`how-it-works-${isDarkMode}`}
              className="text-2xl sm:text-3xl md:text-4xl font-bold font-zonaProUltra mb-6 relative inline-block"
              style={{
                background: sectionHeadingGradient,
                backgroundSize: '200% 100%',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                animation: 'shine 10s linear infinite',
                lineHeight: '1.2',
                paddingBottom: '0.25rem'
              }}
            >
              How Game Your SAT Works
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-700 dark:text-gray-300 leading-relaxed text-left">
              We focus your energy where it counts. Our system tests your knowledge in a fun, score-based system. The more you practice, the better your outcome is likely to be. Keep playing to gain more points. Challenge friends to see who beats everyone to the top of the leaderboard. Take full-length, timed practice tests to chart your progress. Our diagnostics show you where you are strong and where you need to improve.
            </p>
          </motion.div>
        </Card>

        {/* Earn Experience Section */}
        <Card delay={0.3} cardId="earn-experience" className="mb-8 md:mb-12">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <h2
              key={`earn-experience-${isDarkMode}`}
              className="text-2xl sm:text-3xl md:text-4xl font-bold font-zonaProUltra mb-6 relative inline-block"
              style={{
                background: sectionHeadingGradient,
                backgroundSize: '200% 100%',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                animation: 'shine 10s linear infinite',
                lineHeight: '1.2',
                paddingBottom: '0.25rem'
              }}
            >
              Earn Experience, Improve Scores
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-700 dark:text-gray-300 leading-relaxed text-left">
              Practice on your own or challenge friends. Earn points for correct answers and lose points for Incorrect answers. You can build up points to put yourself at the top of the leaderboard.
            </p>
          </motion.div>
        </Card>

        {/* Key Features Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mb-8 md:mb-12"
        >
          <h2
            key={`key-features-${isDarkMode}`}
            className="text-2xl sm:text-3xl md:text-4xl font-bold font-zonaProUltra mb-8 text-center relative inline-block w-full"
            style={{
              background: sectionHeadingGradient,
              backgroundSize: '200% 100%',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              animation: 'shine 10s linear infinite',
              lineHeight: '1.2',
              paddingBottom: '0.25rem'
            }}
          >
            Key Features Built for Focus
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {features.map((feature, index) => (
              <Card key={index} delay={0.4 + index * 0.1} cardId={`feature-${index}`}>
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.7 + index * 0.1 }}
                  className="flex flex-col items-start"
                >
                  <div className="p-3 rounded-xl bg-[#06bae4]/10 dark:bg-[#06bae4]/20 mb-4">
                    <div className="text-[#06bae4]">
                      {feature.icon}
                    </div>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold font-zonaProUltra text-gray-800 dark:text-gray-200 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400 leading-relaxed text-left">
                    {feature.description}
                  </p>
                </motion.div>
              </Card>
            ))}
          </div>
        </motion.div>

        {/* CTA Section */}
        <Card delay={0.8} cardId="cta-section" className="text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
          >
            <h2
              key={`cta-title-${isDarkMode}`}
              className="text-2xl sm:text-3xl md:text-4xl font-bold font-zonaProUltra mb-6 relative inline-block"
              style={{
                background: sectionHeadingGradient,
                backgroundSize: '200% 100%',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                animation: 'shine 10s linear infinite',
                lineHeight: '1.2',
                paddingBottom: '0.25rem'
              }}
            >
              Ready to Transform Your Prep?
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-700 dark:text-gray-300 mb-8 leading-relaxed max-w-3xl mx-auto">
              If you're ready to abandon boring flashcards and unlock a smarter, more engaging path to success on the Digital SAT, your mission starts now.
            </p>
            <p className="text-lg sm:text-xl md:text-2xl font-bold font-zonaProUltra text-gray-800 dark:text-gray-200 mb-8">
              Game Your SAT. Your future score is waiting.
            </p>
            <motion.button
              onClick={() => navigate('/signup')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-4 bg-gradient-to-r from-[#06bae4] to-[#06bae4] hover:from-[#05a5d1] hover:to-[#05a5d1] text-white font-bold font-zonaProUltra rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 text-lg md:text-xl"
            >
              Play Now
            </motion.button>
          </motion.div>
        </Card>
      </div>
    </div>
  );
};

export default AboutGamePage;

